/**
 * SessionSummariesService — Frontend Proxy
 *
 * Calls the secure `generateSessionSummaries` Firebase Cloud Function. The
 * function fetches the RBT draft + best-effort patient context server-side,
 * calls Claude, and persists two parallel summaries onto
 * sessionNote.sessionSummaries:
 *
 *   - supervisorSummary  : clinical, audit-ready (BCBA-facing)
 *   - parentSummary      : warm, plain-language (family-facing)
 *
 * Plus structured fields the UI can surface separately (keyTopics,
 * interventionsUsed, homeworkAssigned, nextSessionFocus, riskFlags).
 *
 * IMPORTANT: The supervisor MUST review and edit the parent summary before
 * sharing it with the family. The AI draft is a starting point, not final.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

export interface SessionSummaries {
  supervisorSummary: string;
  parentSummary: string;
  keyTopics: string[];
  interventionsUsed: string[];
  homeworkAssigned: string[];
  nextSessionFocus: string[];
  riskFlags: string[];
  confidence: number;
  warnings: string[];
  modelUsed: string;
  generatedAt: string;
}

interface GenerateSessionSummariesRequest {
  sessionNoteId: string;
}

interface GenerateSessionSummariesResponse {
  success: boolean;
  data?: SessionSummaries;
  error?: string;
}

class SessionSummariesService {
  /**
   * Generate dual summaries (supervisor + parent) for a session note that
   * already has an RBT draft submitted.
   */
  async generateSummaries(sessionNoteId: string): Promise<SessionSummaries> {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('You must be logged in to generate session summaries');
    }

    const functions = getFunctions();
    const callable = httpsCallable<
      GenerateSessionSummariesRequest,
      GenerateSessionSummariesResponse
    >(functions, 'generateSessionSummaries');

    try {
      const result = await callable({ sessionNoteId });
      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Summary generation failed');
      }
      return result.data.data;
    } catch (error: any) {
      console.error('Error calling generateSessionSummaries:', error);

      if (error.code === 'unauthenticated') {
        throw new Error('You must be logged in to generate session summaries');
      }
      if (error.code === 'permission-denied') {
        throw new Error('You do not have access to this session note');
      }
      if (error.code === 'not-found') {
        throw new Error('Session note not found');
      }
      if (error.code === 'failed-precondition') {
        throw new Error('Session note does not have an RBT draft yet');
      }
      throw new Error(
        error.message || 'Failed to generate session summaries. Please try again.'
      );
    }
  }
}

export const sessionSummariesService = new SessionSummariesService();
